import React, { useMemo, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from 'shared/ui/Card';
import Checkbox from 'shared/ui/Checkbox';
import Select from 'shared/ui/Select';
import CustomPieChart from 'shared/charts/PieChart';
import * as XLSX from 'xlsx'; 
import { useLayout } from 'shared/contexts/LayoutContext';

/** Modal component displaying detailed performance stats for a consultant. */
function ConsultantStatsModal({ consultantData }) {
  if (!consultantData) return null;
  const { openModal } = useLayout();

  const { consultant, total_stats = {}, daily_stats = [] } = consultantData;
  const metaObj = consultant?.meta || null;
  const name    = consultant?.meta?.name || 'Консультант';
  const id      = consultant?.meta?.id || '';

  const totalSum    = total_stats.total_sum || 0;
  const totalProfit = total_stats.total_profit || 0;
  const totalCount  = total_stats.total_count || 0;
  const totalAvg    = total_stats.total_avg_sum || 0;
  const totalDisc   = total_stats.total_discount_sum || 0;
  const totalBonus  = total_stats.total_bonus_spent || 0;

  const marginPct = total_stats.margin_pct != null
    ? Number(total_stats.margin_pct).toFixed(1)
    : totalSum > 0 ? ((totalProfit / totalSum) * 100).toFixed(1) : '0.0';
  const activeDays = total_stats.active_days_count ?? daily_stats.filter((d) => (d.day_count || 0) > 0).length;
  const avgSalesPerDay = total_stats.avg_sales_per_day != null
    ? Number(total_stats.avg_sales_per_day).toFixed(1)
    : activeDays > 0 ? (totalCount / activeDays).toFixed(1) : '0.0';

  const formatCompact = (val) => {
    if (!val) return '0';
    const abs = Math.abs(val);
    if (abs >= 1_000_000) return (val / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (abs >= 1_000)     return (val / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
    return Math.round(val).toLocaleString('ru-RU');
  };

  const formatNum = (val) => (val || val === 0 ? Math.round(val).toLocaleString('ru-RU') : '0');

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-6 text-gray-800 p-1">
      {/* ── Subtitle / Meta Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-blue-50/70 p-4 rounded-xl border border-blue-100">
        <div>
          {metaObj ? (
            <button
              type="button"
              onClick={() => openModal({ meta: metaObj })}
              className="group inline-flex items-center gap-1.5 text-left text-base font-bold text-blue-900 hover:text-blue-700 hover:underline cursor-pointer focus:outline-none"
              title="Открыть карточку консультанта"
            >
              <span>{name}</span>
              <svg className="w-4 h-4 text-blue-600 group-hover:text-blue-700 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
          ) : (
            <h4 className="text-base font-bold text-blue-900">{name}</h4>
          )}
          <p className="text-xs text-blue-600 mt-0.5">ID консультанта: #{id}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
            Рентабельность: {marginPct}%
          </span>
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-800">
            Активных дней: {activeDays} ({avgSalesPerDay} продаж/день)
          </span>
        </div>
      </div>

      {/* ── Key Metrics Cards Grid ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm text-center">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Выручка</p>
          <p className="text-lg font-bold text-gray-900 mt-1">{formatCompact(totalSum)}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">{formatNum(totalSum)} сом</p>
        </div>
        <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm text-center">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Прибыль</p>
          <p className="text-lg font-bold text-green-700 mt-1">{formatCompact(totalProfit)}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">{formatNum(totalProfit)} сом</p>
        </div>
        <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm text-center">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Продажи</p>
          <p className="text-lg font-bold text-indigo-700 mt-1">{formatNum(totalCount)}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">чеков</p>
        </div>
        <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm text-center">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Ср. чек</p>
          <p className="text-lg font-bold text-blue-700 mt-1">{formatCompact(totalAvg)}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">{formatNum(totalAvg)} сом</p>
        </div>
        <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm text-center">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Скидки</p>
          <p className="text-lg font-bold text-amber-700 mt-1">{formatCompact(totalDisc)}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">{formatNum(totalDisc)} сом</p>
        </div>
        <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm text-center">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Бонусы</p>
          <p className="text-lg font-bold text-orange-700 mt-1">{formatCompact(totalBonus)}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">{formatNum(totalBonus)} сом</p>
        </div>
      </div>

      {/* ── Daily Breakdown Table ── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-700">Ежедневная динамика продаж</h4>
          <span className="text-xs text-gray-500 font-medium">Всего дней: {daily_stats.length}</span>
        </div>
        <div className="overflow-x-auto max-h-[320px]">
          <table className="w-full text-xs text-left">
            <thead className="bg-gray-50 border-b text-gray-500 uppercase font-medium sticky top-0">
              <tr>
                <th className="px-4 py-2.5">Дата</th>
                <th className="px-4 py-2.5 text-right">Продажи</th>
                <th className="px-4 py-2.5 text-right">Выручка (сом)</th>
                <th className="px-4 py-2.5 text-right">Прибыль (сом)</th>
                <th className="px-4 py-2.5 text-right">Скидка (сом)</th>
                <th className="px-4 py-2.5 text-right">Бонусы (сом)</th>
                <th className="px-4 py-2.5 text-right">Ср. чек (сом)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-mono">
              {daily_stats.map((day) => (
                <tr key={day.date} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-sans font-medium text-gray-900 whitespace-nowrap">
                    {formatDate(day.date)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-bold text-indigo-700">
                    {formatNum(day.day_count)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-900 font-semibold">
                    {formatNum(day.day_sum)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-green-700">
                    {formatNum(day.day_profit)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-amber-700">
                    {formatNum(day.day_discount_sum)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-orange-700">
                    {formatNum(day.day_bonus_spent)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-blue-700">
                    {formatNum(day.day_avg_sum)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const columns = [
  { key: 'count', label: 'Кол-во', field: 'day_count', totalField: 'total_count' },
  { key: 'sum', label: 'Сумма', field: 'day_sum', totalField: 'total_sum' },
  { key: 'profit', label: 'Прибыль', field: 'day_profit', totalField: 'total_profit' },
  { key: 'discount', label: 'Скидка', field: 'day_discount_sum', totalField: 'total_discount_sum' },
  { key: 'bonus', label: 'Бонусы', field: 'day_bonus_spent', totalField: 'total_bonus_spent' },
  { key: 'avg_sum', label: 'Сред. сумма', field: 'day_avg_sum', totalField: 'total_avg_sum' }
];

const formatTableValue = (val, colKey) => {
  if (val === undefined || val === null || val === '') return '—';
  if (colKey === 'count') {
    return Math.round(val).toLocaleString('ru-RU');
  }
  const roundedK = Math.round(val / 100) / 10;
  return `${roundedK.toLocaleString('ru-RU').replace(',', '.')} k`;
};

const DailyConsultantStats = ({ data: response }) => {
  const { openModal } = useLayout();
  const data = useMemo(
    () => (Array.isArray(response) ? response : (response?.results || [])),
    [response]
  );

  const [visibleColumns, setVisibleColumns] = useState({
    count: true,
    sum: true,
    avg_sum: true,
    profit: false,
    discount: false,
    bonus: false
  });

  const handleConsultantClick = useCallback((consultantData) => {
    if (openModal) {
      openModal({
        title: `Статистика консультанта: ${consultantData.consultant?.meta?.name || ''}`,
        component: <ConsultantStatsModal consultantData={consultantData} />,
      });
    }
  }, [openModal]);
  const [selectedConsultants, setSelectedConsultants] = useState(new Set());
  const [pieChartMetric, setPieChartMetric] = useState('total_sum');
  const [sortBy, setSortBy] = useState('sum');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showDaily, setShowDaily] = useState(true);

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    if (newSortBy === 'id') {
      setSortOrder('asc');
    } else if (sortBy === 'id') {
      setSortOrder('desc');
    }
  };

  const sortedData = useMemo(() => {
    if (!data) return [];
    const list = [...data];
    if (sortBy === 'id') {
      return list.sort((a, b) => {
        const idA = a.consultant?.meta?.id ?? 0;
        const idB = b.consultant?.meta?.id ?? 0;
        return sortOrder === 'asc' ? idA - idB : idB - idA;
      });
    }

    const selectedCol = columns.find(col => col.key === sortBy);
    if (selectedCol) {
      const field = selectedCol.totalField;
      return list.sort((a, b) => {
        const valA = a.total_stats?.[field] ?? 0;
        const valB = b.total_stats?.[field] ?? 0;
        return sortOrder === 'asc' ? valA - valB : valB - valA;
      });
    }
    return list;
  }, [data, sortBy, sortOrder]);

  const dates = useMemo(() => {
    if (Array.isArray(response?.all_dates)) return response.all_dates;
    const uniqueDates = new Set();
    data.forEach(consultantData => {
      (consultantData.daily_stats || []).forEach(stat => {
        uniqueDates.add(stat.date);
      });
    });
    return Array.from(uniqueDates).sort();
  }, [response, data]);

  const dailyStatsMap = useMemo(() => {
    const map = {};
    data.forEach(consultantData => {
      map[consultantData.consultant.meta.id] = {};
      (consultantData.daily_stats || []).forEach(stat => {
        map[consultantData.consultant.meta.id][stat.date] = stat;
      });
    });
    return map;
  }, [data]);

  const pieChartData = useMemo(() => {
    return sortedData
      .filter(d => selectedConsultants.has(d.consultant.meta.id))
      .map(d => ({
        name: d.consultant.meta.name,
        value: d.total_stats[pieChartMetric]
      }));
  }, [selectedConsultants, sortedData, pieChartMetric]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' });
  };

  const handleConsultantSelect = (consultantId) => {
    const newSelected = new Set(selectedConsultants);
    if (newSelected.has(consultantId)) {
      newSelected.delete(consultantId);
    } else {
      newSelected.add(consultantId);
    }
    setSelectedConsultants(newSelected);
  };

  const visibleColumnsList = columns.filter(col => visibleColumns[col.key]);
  
  const exportToExcel = () => {
    // Create first row with consultant, total, and optional dates
    const firstRow = ['Консультант', 'Итого'];
    const secondRow = ['', ...visibleColumnsList.map(col => col.label)];
    
    if (showDaily) {
      dates.forEach(date => {
        visibleColumnsList.forEach(col => {
          firstRow.push(formatDate(date)); 
          secondRow.push(col.label);
        });
      });
    }
  
    // Create data rows
    const dataRows = sortedData.map(consultantData => {
      const row = [
        consultantData.consultant.meta.name,
        ...visibleColumnsList.map(col => 
          consultantData.total_stats[col.totalField]
        )
      ];
      if (showDaily) {
        dates.forEach(date => {
          const stats = dailyStatsMap[consultantData.consultant.meta.id][date] || {};
          visibleColumnsList.forEach(col => row.push(stats[col.field]));
        });
      }
      return row;
    });
  
    // Create worksheet from all rows
    const worksheet = XLSX.utils.aoa_to_sheet([firstRow, secondRow, ...dataRows]);
  
    // Setup merges
    worksheet['!merges'] = [];
    
    // Merge 'Total' header
    worksheet['!merges'].push({
      s: { r: 0, c: 1 },
      e: { r: 0, c: visibleColumnsList.length }
    });
  
    // Calculate and add merge regions for each date if daily view active
    if (showDaily) {
      let currentCol = visibleColumnsList.length + 1;
      dates.forEach(date => {
        worksheet['!merges'].push({
          s: { r: 0, c: currentCol },
          e: { r: 0, c: currentCol + visibleColumnsList.length - 1 }
        });
        currentCol += visibleColumnsList.length;
      });
    }
  
    // Create and save workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'ConsultantStats');
    XLSX.writeFile(workbook, 'ConsultantStats.xlsx');
  };
  return (
    <Card className='space-y-6'>
      <CardHeader>
        <CardTitle>Детальная статистика по консультантам</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Pie Chart for Selected Consultants */}
        {selectedConsultants.size > 0 && (
          <div className='mb-12'>
            <div className='flex items-center gap-4 mb-4'>
              <h3 className='text-lg font-semibold'>Статистика выбранных консультантов по</h3>
              <Select
                value={pieChartMetric}
                onValueChange={setPieChartMetric}
                className='w-48'
              >
                {columns.map(col => (
                  <Select.Option key={col.key} value={col.totalField}>
                    {col.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className='h-96'>
              <CustomPieChart 
                data={pieChartData}
                config={{ colors: ['#8884d8', '#82ca9d', '#ffc658', '#a4de6c', '#ff8042'] }}
              />
            </div>
          </div>
        )}

        {/* Organized Table Control Panel */}
        <div className='mb-6 p-4 bg-gray-50/80 rounded-xl border border-gray-200/80 space-y-4'>
          <div className='flex flex-col lg:flex-row lg:items-center justify-between gap-4'>
            
            {/* Metric Column Toggles */}
            <div className='flex flex-wrap items-center gap-3'>
              <span className='text-xs font-semibold uppercase tracking-wider text-gray-500 mr-1'>
                Метрики:
              </span>
              {columns.map(col => (
                <label 
                  key={col.key} 
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer transition-all border select-none ${
                    visibleColumns[col.key]
                      ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Checkbox
                    id={`toggle-${col.key}`}
                    checked={visibleColumns[col.key]}
                    onChange={(checked) => 
                      setVisibleColumns(prev => ({ ...prev, [col.key]: checked }))
                    }
                  />
                  <span>{col.label}</span>
                </label>
              ))}
            </div>

            {/* Display Options & Actions */}
            <div className='flex flex-wrap items-center gap-4 border-t lg:border-t-0 pt-3 lg:pt-0 border-gray-200'>
              
              {/* Show Daily Toggle Button */}
              <label className='inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50 shadow-sm select-none'>
                <Checkbox
                  checked={showDaily}
                  onChange={(checked) => setShowDaily(checked)}
                />
                <span>По дням</span>
              </label>

              {/* Sort Control */}
              <div className='flex items-center gap-2'>
                <span className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
                  Сортировка:
                </span>
                <Select
                  value={sortBy}
                  onValueChange={handleSortChange}
                  className='w-36'
                >
                  <Select.Option value='id'>По ID</Select.Option>
                  {columns.map(col => (
                    <Select.Option key={col.key} value={col.key}>
                      {col.label}
                    </Select.Option>
                  ))}
                </Select>
                <button
                  type='button'
                  onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                  className='px-2.5 py-1.5 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-100 text-gray-700 text-sm font-bold flex items-center justify-center transition-colors'
                  title={sortOrder === 'asc' ? 'По возрастанию' : 'По убыванию'}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>

              {/* Export Button */}
              <button
                className='flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-1.5 px-4 rounded-lg shadow-sm transition-colors text-sm whitespace-nowrap ml-auto lg:ml-0'
                onClick={exportToExcel}
              >
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                </svg>
                Excel
              </button>

            </div>
          </div>
        </div>

            
        {/* Stats Table */}
        <div className='overflow-x-auto'>
          <table className='min-w-full border-collapse border border-gray-200'>
            <thead className='sticky top-0 bg-white'>
              <tr>
                <th className='px-4 py-2 border border-gray-200 text-left' rowSpan={2}>
                  Консультант
                </th>
                <th className='px-4 py-2 text-center font-semibold border border-gray-200' 
                    colSpan={visibleColumnsList.length}>
                  Итого
                </th>
                {showDaily && dates.map((date) => (
                  <th key={date} 
                      className='px-4 py-2 text-center font-semibold border border-gray-200'
                      colSpan={visibleColumnsList.length}>
                    {formatDate(date)}
                  </th>
                ))}
              </tr>
              <tr>
                {visibleColumnsList.map((col) => (
                  <th key={`total-${col.key}`} 
                      className='px-2 py-1 text-sm font-medium text-gray-600 border border-gray-200'>
                    {col.label}
                  </th>
                ))}
                {showDaily && dates.map((date) => (
                  <React.Fragment key={`metrics-${date}`}>
                    {visibleColumnsList.map((col) => (
                      <th key={`${date}-${col.key}`} 
                          className='px-2 py-1 text-sm font-medium text-gray-600 border border-gray-200'>
                        {col.label}
                      </th>
                    ))}
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((consultantData) => (
                <tr key={consultantData.consultant.meta.id} 
                    className='hover:bg-gray-50'>
                  <td className='px-4 py-2 font-medium border border-gray-200'>
                    <div className='flex items-center gap-2'>
                      <Checkbox
                        checked={selectedConsultants.has(consultantData.consultant.meta.id)}
                        onChange={() => handleConsultantSelect(consultantData.consultant.meta.id)}
                      />
                      <button
                        type="button"
                        onClick={() => handleConsultantClick(consultantData)}
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline font-semibold text-left focus:outline-none group cursor-pointer"
                        title="Нажмите, чтобы открыть статистику консультанта"
                      >
                        <span>{consultantData.consultant.meta.name}</span>
                        <svg className="w-3.5 h-3.5 text-blue-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  </td>
                  {visibleColumnsList.map((col) => (
                    <td key={`total-${col.key}`} 
                        className='px-2 py-1 text-center border border-gray-200'>
                      {formatTableValue(consultantData.total_stats[col.totalField], col.key)}
                    </td>
                  ))}
                  {showDaily && dates.map((date) => {
                    const stats = dailyStatsMap[consultantData.consultant.meta.id][date] || {};
                    return (
                      <React.Fragment key={`${consultantData.consultant.meta.id}-${date}`}>
                        {visibleColumnsList.map((col) => (
                          <td key={`${date}-${col.key}`}
                              className='px-2 py-1 text-center border border-gray-200'>
                            {stats[col.field] ? formatTableValue(stats[col.field], col.key) : '—'}
                          </td>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default DailyConsultantStats;